import { TreeNode } from "@/types/tree";
import { v4 as uuidV4 } from "uuid";
import { breakdanceUndo } from "@/lib/undo/undo";
import {
  findPlaceToAddNodes,
  getNestingRuleFromNode
} from "@/lib/tree/nesting";
import { documentStore, uiStore } from "@/store/store-accessor";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";

export const pasteElements = (
  elements: TreeNode[],
  insertingFromLibraryOrPasting: "inserting" | "pasting" = "inserting",
  pointer?: { nodeId?: TreeNode["id"] },
  options: {
    mode?: "paste" | "replace";
    targetElementId?: TreeNode["id"];
  } = { mode: "paste" }
) => {
  const uniqueId = uuidV4();
  const transactionKey = `pasteElements:${uniqueId}`;
  const { mode = "paste", targetElementId } = options;

  breakdanceUndo.transaction(
    () => {
      elements.forEach(elementToPaste => {
        if (mode === "replace" && targetElementId) {
          const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
            nodeId: undefined
          };

          documentStore.replaceCurrentElementWithCopiedElement({
            targetElementId,
            replacementElement: elementToPaste,
            pointer: pointerForCreatedNodeId
          });

          if (pointerForCreatedNodeId.nodeId) {
            uiStore.activateElement(pointerForCreatedNodeId.nodeId);

            breakdanceStoreActions.run.onCreatedElement({
              elementId: pointerForCreatedNodeId.nodeId,
              transactionKey
            });
          }
          // Only replace the first element if multiple are pasted
          return;
        }

        const nodeToAddId =
          uiStore.activeElementId ||
          uiStore.inlineEditingContext?.elementId ||
          null;

        const placeToAdd = findPlaceToAddNodes(
          documentStore.document.tree,
          nodeToAddId,
          getNestingRuleFromNode(elementToPaste)
        );

        pipe(
          placeToAdd,
          fold(
            () => {},
            legalPlaceToAdd => {
              if (legalPlaceToAdd === "TOP_LEVEL") return;

              const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
                nodeId: undefined
              };

              documentStore.pasteCopiedElementMutation({
                element: elementToPaste,
                placeToAddId: legalPlaceToAdd.place.id,
                placeToAddIndex:
                  legalPlaceToAdd.index ||
                  legalPlaceToAdd.place.children.length,
                pointer: pointerForCreatedNodeId
              });

              if (pointerForCreatedNodeId.nodeId) {
                breakdanceStoreActions.run.onCreatedElement({
                  elementId: pointerForCreatedNodeId.nodeId,
                  transactionKey
                });

                if (pointer) {
                  pointer.nodeId = pointerForCreatedNodeId.nodeId;
                }
              }
            }
          )
        );
      });
    },
    mode === "replace"
      ? `Replace with element`
      : insertingFromLibraryOrPasting === "inserting"
      ? `Import from design library`
      : "Paste Elements",
    transactionKey
  );
};

export function getLastUsedDesignProviderUrl(): string | false {
  const maybeProviderUrl = readFromLocalStorage(
    "breakdance_last_used_design_provider"
  );
  if (typeof maybeProviderUrl === "string") {
    return maybeProviderUrl;
  }

  return false;
}

export function setLastUsedDesignProviderUrl(providerUrl: string) {
  writeToLocalStorage("breakdance_last_used_design_provider", providerUrl);
}
